import * as React from 'react'
import { MinusIcon } from 'lucide-react'

import { cn } from '../../lib/utils'

interface InputOTPProps {
  value: string
  onChange: (value: string) => void
  length?: number
  disabled?: boolean
  autoFocus?: boolean
  className?: string
  containerClassName?: string
  hasError?: boolean
  separator?: React.ReactNode
  placeholder?: string
  name?: string
  'data-testid'?: string
}

function InputOTP({
  value,
  onChange,
  length = 6,
  disabled = false,
  autoFocus = false,
  className,
  containerClassName,
  hasError = false,
  separator,
  name = 'otp',
  'data-testid': dataTestId,
}: InputOTPProps) {
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([])
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1)
  const [isAutofilling, setIsAutofilling] = React.useState(false)
  const autofillTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  // Initialize refs array
  React.useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length)
  }, [length])

  // Auto-focus first input if autoFocus is true
  React.useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [autoFocus])

  // Global keydown listener for error state
  React.useEffect(() => {
    if (!hasError) return

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Backspace') {
        e.preventDefault()
        onChange('')
        inputRefs.current[0]?.focus()
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [hasError, onChange])

  // Handle autofill detection
  React.useEffect(() => {
    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement
      if (target && target.name === name && target.value.length >= 1) {
        const autofillValue = target.value.replace(/[^0-9]/g, '').slice(0, length)
        if (autofillValue.length > 0 && autofillValue !== value) {
          // Clear any existing timeout
          if (autofillTimeoutRef.current) {
            clearTimeout(autofillTimeoutRef.current)
          }

          setIsAutofilling(true)
          onChange(autofillValue)

          // Focus the last filled input
          const lastFilledIndex = Math.min(autofillValue.length - 1, length - 1)
          autofillTimeoutRef.current = setTimeout(() => {
            if (!disabled && inputRefs.current[lastFilledIndex]) {
              inputRefs.current[lastFilledIndex].focus()
            }
            setIsAutofilling(false)
            autofillTimeoutRef.current = null
          }, 50)
        }
      }
    }

    document.addEventListener('input', handleInput, true)
    return () => {
      document.removeEventListener('input', handleInput, true)
      if (autofillTimeoutRef.current) {
        clearTimeout(autofillTimeoutRef.current)
        autofillTimeoutRef.current = null
      }
    }
  }, [name, length, onChange, value, disabled])

  const handleInputChange = (index: number, inputValue: string) => {
    if (disabled || isAutofilling) return

    // Only allow numeric input
    const numericValue = inputValue.replace(/[^0-9]/g, '')

    // If no numeric value, clear the current position
    if (!numericValue) {
      const newValue = value.split('')
      newValue[index] = ''
      onChange(newValue.join(''))
      return
    }

    // Handle multiple digits being pasted (Android autofill/paste)
    if (numericValue.length > 1) {
      const newValue = value.split('')
      for (let i = 0; i < numericValue.length && index + i < length; i++) {
        newValue[index + i] = numericValue[i]
      }
      onChange(newValue.join(''))

      // Focus the next empty input or the last input
      const nextIndex = Math.min(index + numericValue.length, length - 1)
      inputRefs.current[nextIndex]?.focus()
      return
    }

    // Single character input
    const char = numericValue

    // Update the value
    const newValue = value.split('')
    newValue[index] = char
    const result = newValue.join('').slice(0, length)

    onChange(result)

    // Auto-advance to next input if character was entered and not at the end
    if (char && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    // Skip backspace handling if there's an error (handled globally)
    if (e.key === 'Backspace' && hasError) {
      return
    }

    if (e.key === 'Backspace') {
      e.preventDefault()
      // If current input is empty, go to previous input and clear it
      if (!value[index]) {
        if (index > 0) {
          const newValue = value.split('')
          newValue[index - 1] = ''
          onChange(newValue.join(''))
          inputRefs.current[index - 1]?.focus()
        }
      } else {
        // Clear current input
        const newValue = value.split('')
        newValue[index] = ''
        onChange(newValue.join(''))
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault()
      inputRefs.current[index + 1]?.focus()
    } else if (e.key === 'Home') {
      e.preventDefault()
      inputRefs.current[0]?.focus()
    } else if (e.key === 'End') {
      e.preventDefault()
      inputRefs.current[length - 1]?.focus()
    }
  }

  const handleFocus = (index: number, e: React.FocusEvent<HTMLInputElement>) => {
    setFocusedIndex(index)

    // If there's an error and user focuses any input, clear all and focus first
    if (hasError) {
      onChange('')
      e.preventDefault()
      inputRefs.current[0]?.focus()
      return
    }

    // Select all text when focusing on an input with a value
    const input = inputRefs.current[index]
    if (input && input.value) {
      // Use setTimeout to ensure the focus is complete before selecting
      setTimeout(() => {
        input.select()
      }, 0)
    }
  }

  const handleBlur = () => {
    setFocusedIndex(-1)
  }

  const handleClick = (index: number) => {
    if (disabled) return

    // If there's an error and user clicks any input, clear all and focus first
    if (hasError) {
      onChange('')
      inputRefs.current[0]?.focus()
      return
    }

    const input = inputRefs.current[index]
    if (input) {
      input.focus()
      // Select all text if the input has a value, making it easy to replace
      if (input.value) {
        input.select()
      }
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    if (disabled) return

    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain').slice(0, length)

    // Only allow numeric input
    const filteredData = pastedData.replace(/[^0-9]/g, '')

    if (filteredData) {
      onChange(filteredData.padEnd(length, ''))

      // Focus the next empty input or the last input
      const nextIndex = Math.min(filteredData.length, length - 1)
      inputRefs.current[nextIndex]?.focus()
    }
  }

  // Android-specific paste handler: On Android, the "suggested paste" feature from authenticator apps
  // triggers paste events directly on individual input elements rather than bubbling to the container.
  // Without this handler, the maxLength={1} attribute would truncate multi-digit OTP codes to a single character.
  // This handler intercepts paste events on each input and properly distributes all digits across the fields.
  const handleInputPaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return

    e.preventDefault()
    e.stopPropagation()

    const pastedData = e.clipboardData.getData('text/plain').slice(0, length)

    // Only allow numeric input
    const filteredData = pastedData.replace(/[^0-9]/g, '')

    if (filteredData) {
      // If multiple digits are pasted, fill from current index
      if (filteredData.length > 1) {
        const newValue = value.split('')
        for (let i = 0; i < filteredData.length && index + i < length; i++) {
          newValue[index + i] = filteredData[i]
        }
        onChange(newValue.join('').padEnd(length, ''))

        // Focus the next empty input or the last input
        const nextIndex = Math.min(index + filteredData.length, length - 1)
        inputRefs.current[nextIndex]?.focus()
      } else if (filteredData.length === 1) {
        // Single digit paste
        const newValue = value.split('')
        newValue[index] = filteredData
        onChange(newValue.join('').padEnd(length, ''))

        if (index < length - 1) {
          inputRefs.current[index + 1]?.focus()
        }
      }
    }
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2',
        disabled && 'opacity-50 pointer-events-none',
        containerClassName,
      )}
      onPaste={handlePaste}
    >
      {/* Primary input for autofill - this is the main input that password managers will target */}
      <input
        type="text"
        name={name}
        autoComplete="one-time-code"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onChange={(e) => {
          const autofillValue = e.target.value.replace(/[^0-9]/g, '').slice(0, length)
          if (autofillValue !== value) {
            onChange(autofillValue)
          }
        }}
        style={{
          position: 'absolute',
          left: '-9999px',
          opacity: 0,
          pointerEvents: 'none',
          width: '1px',
          height: '1px',
          padding: 0,
          border: 'none',
          outline: 'none',
        }}
        tabIndex={-1}
        aria-hidden="true"
        data-testid={dataTestId ? `${dataTestId}-hidden` : undefined}
      />
      {Array.from({ length }, (_, index) => (
        <React.Fragment key={index}>
          <input
            ref={(el) => {
              inputRefs.current[index] = el
            }}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            value={value[index] || ''}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={(e) => handleFocus(index, e)}
            onBlur={handleBlur}
            onClick={() => handleClick(index)}
            onPaste={(e) => handleInputPaste(index, e)}
            onBeforeInput={(e) => {
              // Capture full autofill value before maxLength truncation
              const nativeEvent = e.nativeEvent as InputEvent
              if (nativeEvent.data && nativeEvent.data.length > 1 && !disabled && !isAutofilling) {
                const numericValue = nativeEvent.data.replace(/[^0-9]/g, '')
                if (numericValue.length > 1) {
                  e.preventDefault()
                  const newValue = value.split('')
                  for (let i = 0; i < numericValue.length && index + i < length; i++) {
                    newValue[index + i] = numericValue[i]
                  }
                  onChange(newValue.join(''))
                  const nextIndex = Math.min(index + numericValue.length, length - 1)
                  setTimeout(() => {
                    inputRefs.current[nextIndex]?.focus()
                  }, 0)
                }
              }
            }}
            onInput={(e) => {
              // Prevent non-numeric input and handle autofill
              const target = e.target as HTMLInputElement
              const inputValue = target.value
              const numericValue = inputValue.replace(/[^0-9]/g, '')

              // If multiple digits somehow made it through (autofill), handle them
              if (numericValue.length > 1 && !disabled && !isAutofilling) {
                const newValue = value.split('')
                for (let i = 0; i < numericValue.length && index + i < length; i++) {
                  newValue[index + i] = numericValue[i]
                }
                onChange(newValue.join(''))
                target.value = numericValue[0] || ''
                const nextIndex = Math.min(index + numericValue.length, length - 1)
                setTimeout(() => {
                  inputRefs.current[nextIndex]?.focus()
                }, 0)
              } else if (target.value !== numericValue) {
                target.value = numericValue
              }
            }}
            disabled={disabled}
            name={`${name}-${index}`}
            autoComplete="off"
            data-testid={dataTestId ? `${dataTestId}-${index}` : undefined}
            className={cn(
              'relative flex h-9 w-9 items-center justify-center text-lg transition-all outline-none bg-gray-100 rounded-[12px] h-[60px] w-[51px] text-center',
              'border-2 border-transparent',
              'focus:border-ring focus:ring-ring/50 focus:ring-[3px] focus:z-10',
              'aria-invalid:border-destructive focus:aria-invalid:border-destructive focus:aria-invalid:ring-destructive/20',
              'dark:bg-input/30',
              'disabled:cursor-not-allowed',
              focusedIndex === index && 'border-ring ring-ring/50 ring-[3px] z-10',
              hasError && ' bg-destructive/10',
              className,
            )}
            aria-label={`Digit ${index + 1} of ${length}`}
          />
          {separator && index < length - 1 && (
            <div className="flex items-center justify-center text-muted-foreground">
              {separator}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

// Keep the old component names for backward compatibility
const InputOTPGroup = InputOTP
const InputOTPSlot = ({
  index: _index,
  className,
  ...props
}: React.ComponentProps<'div'> & { index: number }) => (
  <div className={cn('relative', className)} {...props} />
)
const InputOTPSeparator = ({ ...props }: React.ComponentProps<'div'>) => (
  <div role="separator" {...props}>
    <MinusIcon />
  </div>
)

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator }
